/**
 * Episodes module
 * Handles episode list, filtering, and actions
 */

import { API } from '../core/api.js';
import { AppState } from '../core/state.js';
import { showNotification } from '../components/notifications.js';

// Pagination settings
const episodesPerPage = 50;

// Multi-tag filter state
let filterTags = [];
let availableTags = []; // Cache of tags from loaded episodes

/**
 * Update URL with current episode page and filters
 * URL pattern: #/podcast/{podcastId}/episodes?page=1&search=term&tags=ai,tech
 */
function updateEpisodePageURL(podcastId, podcastTitle, page, filters = {}) {
    const params = new URLSearchParams();
    params.set('page', page + 1);  // 1-indexed for user-friendly display
    params.set('title', podcastTitle);

    if (filters.search) params.set('search', filters.search);
    if (filters.status) params.set('status', filters.status);
    if (filters.tags) params.set('tags', filters.tags);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.sort && filters.sort !== 'newest') params.set('sort', filters.sort);

    const newUrl = `#/podcast/${podcastId}/episodes?${params.toString()}`;
    history.pushState({ podcastId, page, filters }, '', newUrl);
}

/**
 * Parse episode page URL and load episodes
 * Called from app.js route handler
 */
export function handleEpisodePageFromURL(hash) {
    // Parse: #/podcast/123/episodes?page=2&search=term
    const match = hash.match(/#\/podcast\/(\d+)\/episodes\??(.*)/);
    if (!match) return false;

    const podcastId = parseInt(match[1]);
    const queryString = match[2] || '';
    const params = new URLSearchParams(queryString);

    const page = Math.max(0, parseInt(params.get('page') || '1') - 1);  // Convert to 0-indexed
    const title = params.get('title') || `Podcast ${podcastId}`;

    const filters = {};
    if (params.get('search')) filters.search = params.get('search');
    if (params.get('status')) filters.status = params.get('status');
    if (params.get('tags')) filters.tags = params.get('tags');
    if (params.get('date_from')) filters.date_from = params.get('date_from');
    if (params.get('date_to')) filters.date_to = params.get('date_to');
    if (params.get('sort')) filters.sort = params.get('sort');

    // Restore filter tags state
    filterTags = filters.tags ? filters.tags.split(',') : [];

    // Load episodes with parsed state (don't update URL again)
    loadEpisodesInternal(podcastId, title, page, filters);
    return true;
}

/**
 * Generate pagination button array with ellipsis
 * Returns array of: numbers (page indices) or '...' for ellipsis
 * Always shows exactly 9 elements for stable layout
 * Pattern: 1 ... 5 6 [7] 8 9 ... 33 (5 page numbers always visible)
 */
function generatePaginationButtons(currentPage, totalPages) {
    if (totalPages <= 9) {
        // Show all pages if 9 or fewer
        return Array.from({ length: totalPages }, (_, i) => i);
    }

    const first = 0;
    const last = totalPages - 1;

    // Calculate center of the 5-page window around current
    // Window: [center-2, center-1, center, center+1, center+2]
    let center = currentPage;

    // Clamp center so the 5-page window fits between first and last
    // center-2 must be >= 1 (after first), center+2 must be <= last-1 (before last)
    if (center < 3) {
        center = 3;  // Window: 1,2,3,4,5 (indices)
    } else if (center > last - 3) {
        center = last - 3;  // Window ends at last-1
    }

    // Build result: always 9 elements
    // [first] [...] [c-2] [c-1] [c] [c+1] [c+2] [...] [last]
    return [
        first,
        '...',
        center - 2,
        center - 1,
        center,
        center + 1,
        center + 2,
        '...',
        last
    ];
}

/**
 * Render pagination HTML with page number buttons
 */
function renderPaginationHTML(podcastId, podcastTitle, currentPage, totalPages) {
    if (totalPages <= 1) return '';

    const buttons = generatePaginationButtons(currentPage, totalPages);
    const escapedTitle = podcastTitle.replace(/'/g, "\\'");

    let html = '<div class="pagination" style="margin: 1rem 0; display: flex; justify-content: center; gap: 0.25rem; align-items: center; flex-wrap: wrap;">';

    // Previous button
    html += `<button class="btn btn-small pagination-btn ${currentPage === 0 ? 'btn-disabled' : ''}"
                     onclick="navigateEpisodePage(${podcastId}, '${escapedTitle}', ${currentPage - 1})"
                     ${currentPage === 0 ? 'disabled' : ''}>←</button>`;

    // Page number buttons
    for (const btn of buttons) {
        if (btn === '...') {
            html += '<span class="pagination-ellipsis">...</span>';
        } else {
            const isActive = btn === currentPage;
            html += `<button class="btn btn-small pagination-btn ${isActive ? 'pagination-btn-active' : ''}"
                             onclick="navigateEpisodePage(${podcastId}, '${escapedTitle}', ${btn})"
                             ${isActive ? 'aria-current="page"' : ''}>${btn + 1}</button>`;
        }
    }

    // Next button
    html += `<button class="btn btn-small pagination-btn ${currentPage >= totalPages - 1 ? 'btn-disabled' : ''}"
                     onclick="navigateEpisodePage(${podcastId}, '${escapedTitle}', ${currentPage + 1})"
                     ${currentPage >= totalPages - 1 ? 'disabled' : ''}>→</button>`;

    html += '</div>';
    return html;
}

/**
 * Navigate to a specific episode page (called from pagination buttons)
 */
export function navigateEpisodePage(podcastId, podcastTitle, page) {
    const filters = buildEpisodeFilters();
    updateEpisodePageURL(podcastId, podcastTitle, page, filters);
    loadEpisodesInternal(podcastId, podcastTitle, page, filters);
}

/**
 * Internal function to load episodes (doesn't update URL)
 */
async function loadEpisodesInternal(podcastId, podcastTitle, page, filters) {
    try {
        AppState.currentPodcastId = podcastId;
        AppState.currentPodcastTitle = podcastTitle;
        AppState.currentEpisodePage = page;

        // For global access
        window.currentPodcastId = podcastId;
        window.currentPodcastTitle = podcastTitle;

        const offset = page * episodesPerPage;

        // Build query string with filters
        let queryParams = `limit=${episodesPerPage}&offset=${offset}`;

        if (filters.search) queryParams += `&search=${encodeURIComponent(filters.search)}`;
        if (filters.date_from) queryParams += `&date_from=${filters.date_from}`;
        if (filters.date_to) queryParams += `&date_to=${filters.date_to}`;
        if (filters.tags) queryParams += `&tags=${encodeURIComponent(filters.tags)}`;
        if (filters.sort) queryParams += `&sort=${filters.sort}`;

        const episodesData = await API.get(`/api/podcasts/${podcastId}/episodes?${queryParams}`);

        const episodes = episodesData.episodes || [];
        const total = episodesData.total || 0;
        const totalPages = Math.ceil(total / episodesPerPage);

        const container = document.getElementById('podcasts');
        if (!container) return;

        container.innerHTML = `
            <button class="btn btn-secondary" onclick="loadPodcasts(${AppState.currentPodcastPage})" style="margin-bottom: 1rem;">← Back to Podcasts</button>
            <h2 style="margin-bottom: 0.75rem;">${podcastTitle}</h2>

            <!-- Podcast Actions (visible on mobile) -->
            <div class="podcast-actions-mobile">
                <button class="btn btn-small btn-secondary" onclick="fetchPodcastHistory(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}')">
                    📥 Fetch Episodes
                </button>
                <button class="btn btn-small btn-primary" onclick="fetchAllTranscriptions(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}', event.shiftKey)" title="Shift+Click to force refresh">
                    📝 Transcriptions
                </button>
                <button class="btn btn-small btn-warning" onclick="checkAndAddDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}')">
                    🎤 Diarization
                </button>
                <button class="btn btn-small btn-secondary" onclick="viewMissingDiarization(${podcastId}, '${podcastTitle.replace(/'/g, "\\'")}')">
                    🔍 Missing
                </button>
                <button class="viz-trigger-btn" onclick="showPodcastVisualizations(${podcastId})" title="View word cloud and analytics">
                    ✨ Visualize
                </button>
            </div>

            <!-- Search and Filter Bar -->
            <div class="filter-bar" style="margin-bottom: 1.5rem;">
                <div class="filter-row" style="display: flex; gap: 0.5rem; align-items: center;">
                    <input type="text" id="episode-search" class="filter-input" placeholder="🔍 Search..."
                           value="${filters.search || ''}"
                           onkeyup="if(event.key === 'Enter') applyEpisodeFilters()" style="flex: 2; min-width: 150px;">

                    <select id="filter-status" class="filter-select" onchange="applyEpisodeFilters()" style="min-width: 120px;">
                        <option value="">All Status</option>
                        <option value="unplayed">Unplayed</option>
                        <option value="played">Played</option>
                        <option value="processing">Processing</option>
                        <option value="failed">Failed</option>
                        <option value="missing_transcription">No Transcription</option>
                        <option value="missing_llm">No Analytics</option>
                    </select>

                    <select id="filter-date-range" class="filter-select" onchange="applyEpisodeFilters()" style="min-width: 100px;">
                        <option value="">All Time</option>
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="3months">3 Months</option>
                        <option value="year">Year</option>
                    </select>

                    <select id="filter-sort" class="filter-select" onchange="applyEpisodeFilters()" style="min-width: 100px;">
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                    </select>

                    <select id="filter-tag-add" class="filter-select" onchange="addFilterTag(this.value); this.value='';" style="min-width: 80px;">
                        <option value="">+ Tag</option>
                    </select>

                    <button class="btn btn-small" onclick="clearEpisodeFilters()">Clear</button>
                </div>
                <div class="filter-tags-row" id="filter-tags-row" style="display: none; margin-top: 0.5rem; gap: 0.5rem;">
                    <div id="filter-selected-tags" class="filter-selected-tags"></div>
                </div>
            </div>

            <div style="text-align: center; margin-bottom: 0.5rem; color: var(--text-secondary);">
                Showing ${episodes.length} of ${total} episode${total !== 1 ? 's' : ''}
            </div>
            ${renderPaginationHTML(podcastId, podcastTitle, page, totalPages)}
            <div id="episodes-list"></div>
            ${renderPaginationHTML(podcastId, podcastTitle, page, totalPages)}
        `;

        const episodesList = document.getElementById('episodes-list');

        // Populate tag filter dropdown from loaded episodes (no extra API call)
        populateTagAddDropdown(episodes);
        renderFilterTags();

        // Restore filter dropdown states
        if (filters.status) {
            const statusSelect = document.getElementById('filter-status');
            if (statusSelect) statusSelect.value = filters.status;
        }
        if (filters.sort) {
            const sortSelect = document.getElementById('filter-sort');
            if (sortSelect) sortSelect.value = filters.sort;
        }

        if (episodes.length === 0 && page === 0) {
            episodesList.innerHTML = '<p style="color: var(--text-secondary);">No episodes found.</p>';
            return;
        }

        episodesList.innerHTML = episodes.map(ep => {
            const tagsHtml = (ep.tags && ep.tags.length > 0)
                ? `<div class="episode-tags">${ep.tags.map(t => `<span class="tag-chip tag-chip-small" onclick="event.stopPropagation(); filterByTag('${t.replace(/'/g, "\\'")}')">${t}</span>`).join('')}</div>`
                : '';

            // Check if there are action buttons to show
            const hasActions = !ep.downloaded || (ep.downloaded && !ep.processed);

            return `
                <div class="episode-item episode-item-clickable"
                     onclick="viewEpisodeDetails(${ep.id})"
                     role="button"
                     tabindex="0"
                     onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();viewEpisodeDetails(${ep.id})}">
                    <div class="episode-header">
                        <div class="episode-title">${ep.title}</div>
                        ${hasActions ? `
                        <div class="episode-actions" onclick="event.stopPropagation()">
                            ${!ep.downloaded ? `<button class="btn btn-small btn-compact" onclick="downloadEpisode(${ep.id})">Download</button>` : ''}
                            ${ep.downloaded && !ep.processed ? `<button class="btn btn-small btn-compact" onclick="processEpisode(${ep.id})">Process</button>` : ''}
                        </div>
                        ` : ''}
                    </div>
                    <div class="episode-meta">
                        <span class="episode-date" title="Published date">${ep.published_date ? new Date(ep.published_date).toLocaleDateString() : 'Unknown'}</span>
                        ${ep.duration_seconds ? `<span class="episode-duration">${Math.floor(ep.duration_seconds / 60)}m</span>` : ''}
                        <span class="status-icons">${getStatusIcons(ep)}</span>
                    </div>
                    ${tagsHtml}
                </div>
            `;
        }).join('');
    } catch (error) {
        console.error('Error loading episodes:', error);
    }
}

/**
 * Generate compact icon-only status indicators for episode list
 * @param {Object} ep - Episode object
 * @returns {string} HTML string of status icons
 */
function getStatusIcons(ep) {
    const icons = [];

    // Played status icon (first position - most important for user)
    if (ep.played) {
        icons.push(`<span class="status-icon status-icon-success" title="Played - Click to mark unplayed" aria-label="Played" onclick="event.stopPropagation(); togglePlayedStatus(${ep.id}, true)" style="cursor: pointer;">✓</span>`);
    } else {
        icons.push(`<span class="status-icon status-icon-pending" title="Unplayed - Click to mark as played" aria-label="Unplayed" onclick="event.stopPropagation(); togglePlayedStatus(${ep.id}, false)" style="cursor: pointer;">○</span>`);
    }

    // Download status icon
    if (ep.downloaded) {
        icons.push(`<span class="status-icon status-icon-downloaded" title="Downloaded" aria-label="Downloaded">⬇</span>`);
    }

    // Transcription status icon
    const status = ep.transcription_status || 'pending';
    const statusMap = {
        'completed': ['status-icon-success', '✓', 'Transcribed'],
        'processing': ['status-icon-processing', '⏳', 'Processing'],
        'failed': ['status-icon-error', '✗', 'Failed'],
        'not_available': ['status-icon-unavailable', '⚠', 'Not available'],
        'pending': ['status-icon-pending', '○', 'Pending']
    };
    const [cls, icon, label] = statusMap[status] || statusMap.pending;
    icons.push(`<span class="status-icon ${cls}" title="${label}" aria-label="${label}">${icon}</span>`);

    return icons.join('');
}

/**
 * Load episodes for a podcast with pagination and filters
 * Updates URL and delegates to internal function
 */
export async function loadEpisodes(podcastId, podcastTitle, page = 0, filters = {}) {
    // Update URL with current state
    updateEpisodePageURL(podcastId, podcastTitle, page, filters);
    // Load episodes
    await loadEpisodesInternal(podcastId, podcastTitle, page, filters);
}

/**
 * Apply episode filters from UI
 */
export function applyEpisodeFilters() {
    if (!AppState.currentPodcastId) return;

    const filters = buildEpisodeFilters();
    loadEpisodes(AppState.currentPodcastId, AppState.currentPodcastTitle, 0, filters);
}

/**
 * Clear all episode filters
 */
export function clearEpisodeFilters() {
    if (!AppState.currentPodcastId) return;

    const searchInput = document.getElementById('episode-search');
    const statusFilter = document.getElementById('filter-status');
    const dateRangeFilter = document.getElementById('filter-date-range');
    const sortFilter = document.getElementById('filter-sort');

    if (searchInput) searchInput.value = '';
    if (statusFilter) statusFilter.value = '';
    if (dateRangeFilter) dateRangeFilter.value = '';
    if (sortFilter) sortFilter.value = 'newest';

    // Clear multi-tag selection
    filterTags = [];

    loadEpisodes(AppState.currentPodcastId, AppState.currentPodcastTitle, 0, {});
}

/**
 * Build filter object from UI inputs
 */
export function buildEpisodeFilters() {
    const filters = {};

    const searchInput = document.getElementById('episode-search');
    const statusFilter = document.getElementById('filter-status');
    const dateRangeFilter = document.getElementById('filter-date-range');

    if (searchInput && searchInput.value.trim()) {
        filters.search = searchInput.value.trim();
    }

    // Status filter
    if (statusFilter && statusFilter.value) {
        filters.status = statusFilter.value;
    }

    // Multi-tag filter
    if (filterTags.length > 0) {
        filters.tags = filterTags.join(',');
    }

    if (dateRangeFilter && dateRangeFilter.value) {
        const now = new Date();
        const ranges = {
            'week': 7,
            'month': 30,
            '3months': 90,
            'year': 365
        };

        const daysAgo = ranges[dateRangeFilter.value];
        if (daysAgo) {
            const fromDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
            filters.date_from = fromDate.toISOString().split('T')[0];
        }
    }

    // Sort order
    const sortFilter = document.getElementById('filter-sort');
    if (sortFilter && sortFilter.value) {
        filters.sort = sortFilter.value;
    }

    return filters;
}

/**
 * Populate tag add dropdown with available tags (excluding already selected)
 * Extracts tags from loaded episodes instead of making API call
 */
function populateTagAddDropdown(episodes = null) {
    const dropdown = document.getElementById('filter-tag-add');
    if (!dropdown) return;

    // If episodes provided, extract and cache unique tags
    if (episodes && episodes.length > 0) {
        const tagSet = new Set();
        episodes.forEach(ep => {
            if (ep.tags && Array.isArray(ep.tags)) {
                ep.tags.forEach(t => tagSet.add(t));
            }
        });
        availableTags = [...tagSet].sort();
    }

    // Clear and rebuild options
    dropdown.textContent = '';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = '+ Add';
    dropdown.appendChild(defaultOption);

    // Only show tags not already selected
    availableTags.filter(tag => !filterTags.includes(tag)).forEach(tag => {
        const option = document.createElement('option');
        option.value = tag;
        option.textContent = tag;
        dropdown.appendChild(option);
    });
}

/**
 * Render selected filter tags as chips
 */
function renderFilterTags() {
    const container = document.getElementById('filter-selected-tags');
    const tagsRow = document.getElementById('filter-tags-row');
    if (!container) return;

    container.textContent = '';

    // Show/hide tags row based on whether there are selected tags
    if (tagsRow) {
        tagsRow.style.display = filterTags.length > 0 ? 'flex' : 'none';
    }

    filterTags.forEach(tag => {
        const chip = document.createElement('span');
        chip.className = 'filter-tag-chip';
        chip.textContent = tag;

        const removeBtn = document.createElement('button');
        removeBtn.textContent = '×';
        removeBtn.title = 'Remove';
        removeBtn.onclick = () => removeFilterTag(tag);
        chip.appendChild(removeBtn);

        container.appendChild(chip);
    });
}

/**
 * Add a tag to the filter
 */
export function addFilterTag(tag) {
    if (!tag || filterTags.includes(tag)) return;

    filterTags.push(tag);
    renderFilterTags();
    populateTagAddDropdown();
    applyEpisodeFilters();
}

/**
 * Remove a tag from the filter
 */
export function removeFilterTag(tag) {
    filterTags = filterTags.filter(t => t !== tag);
    renderFilterTags();
    populateTagAddDropdown();
    applyEpisodeFilters();
}

/**
 * Filter episodes by clicking a tag in the episode list
 */
export function filterByTag(tag) {
    if (!filterTags.includes(tag)) {
        addFilterTag(tag);
    }
}

/**
 * Download an episode
 */
export async function downloadEpisode(episodeId, buttonElement = null) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Downloading...';

    try {
        const response = await fetch(`/api/episodes/${episodeId}/download`, { method: 'POST' });

        if (response.ok) {
            button.innerHTML = '✅ Downloaded';
            setTimeout(() => location.reload(), 1000);
        } else {
            button.innerHTML = '❌ Error';
        }
    } catch (error) {
        console.error('Error downloading episode:', error);
        button.innerHTML = '❌ Error';
    }
}

/**
 * Process (transcribe) an episode
 */
export async function processEpisode(episodeId, buttonElement = null) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Processing...';

    try {
        const response = await fetch(`/api/episodes/${episodeId}/process`, { method: 'POST' });

        if (response.ok) {
            button.innerHTML = '✅ Processed';
            setTimeout(() => location.reload(), 1000);
        } else {
            button.innerHTML = '❌ Error';
        }
    } catch (error) {
        console.error('Error processing episode:', error);
        button.innerHTML = '❌ Error';
    }
}

/**
 * Toggle played status for an episode
 */
export async function togglePlayedStatus(episodeId, currentStatus) {
    try {
        const formData = new FormData();
        formData.append('played', !currentStatus);

        const response = await fetch(`/api/episodes/${episodeId}/played`, {
            method: 'PUT',
            body: formData
        });

        if (response.ok) {
            // Reload episode list to show updated status
            loadEpisodesInternal(
                AppState.currentPodcastId,
                AppState.currentPodcastTitle,
                AppState.currentEpisodePage
            );
        } else {
            showNotification('Failed to update played status', 'error');
        }
    } catch (error) {
        console.error('Error updating played status:', error);
        showNotification('Error updating played status', 'error');
    }
}
